import React, { useEffect } from "react";
import { Row, Col, Container } from "react-bootstrap";
import PropTypes from 'prop-types';
import { object, string, mixed, array as yuparray } from 'yup';
import { FormControl, FormLabel, Button, Card, CardContent } from '@mui/material';
import { CheckboxInput, TextInput, SelectInput, StyledRadioInput } from "../../../Helpers/Inputs";
import InsuranceProviders from '../../../Helpers/Lists/InsuranceProviders';
import { FLORIDA, MASSACHUSETTS, WYOMING } from '../../../Helpers/Constants/States';
import { NumberFormatDollars, InputMaskDate } from '../../../Helpers/Formatters';
import { ConvertStringToBoolean, ConvertBooleanToString } from "../../../Helpers/Formatters/ConvertBoolean";
import { emptyStringToUndefined } from '../../../Helpers/Formatters/EmptyStringToUndefined.jsx';
import Step7Icon from '../icons/step_7.svg?react';
import { isBefore, isAfter, isValid } from 'date-fns';
import IconHolder from "../../../Helpers/IconHolder";
import './PolicyPurposeConfirmation.scss'

const stateList = ["AK", "AL", "AR", "AZ", "CO", "CT", "DE", "GA", "HI", "IA", "ID", "IL", "IN", "KY", "LA", "MD", "ME", "MN", "MO", "MS", "MT", "NC", "NE", "NH", "NJ", "NM", "OH", "OR", "RI", "SC", "SD", "TN", "TX", "UT", "VA", "VT", "WI", "WV"];
const excludeReplacingReasonStates = ["CA", "DC", "FL", "KS", "MA", "MI", "ND", "NV", "OK", "PA", "WA", "WY"];
export default function PolicyPurposeConfirmation(props) {

  const { register, setValue, trigger } = props;

  const state = props.fullFormState.steps.basicInfo && props.fullFormState.steps.basicInfo.state;

  const handleValueChange = (field, value) => {
    props.onFieldChange(field, value);
    trigger(field);
  };

  useEffect(() => {
    register('state');
    setValue('state', state);
  }, [register]);

  const addNewPolicy = () => {
    const newForm = PolicyPurposeConfirmation.DefaultFormState().policies[0];
    props.onFieldChange('policies', [...props.formState.policies, newForm]);
  };

  const formPolicies = props.formState.policies.map((policyFormState, index) =>
    <PolicyInformation
      formState={policyFormState}
      onRemove={() => {
        props.onFieldChange(
          'policies',
          props.formState.policies.filter((form, formIndex) => formIndex !== index)
        );
        setValue('policies', props.formState.policies.filter((form, formIndex) => formIndex !== index))
      }}
      onChange={(newForm) => {
        props.onFieldChange(
          'policies',
          props.formState.policies.map((form, formIndex) => {
            return formIndex === index ? newForm : form;
          })
        );
      }}
      trigger={trigger}
      key={index}
      index={index}
      showPolicyRemove={props.formState.policies.length > 1}
      useExistingPolicyFunds={props.formState.useExistingPolicyFunds}
      state={state}
    />
  );

  return (
    <Container>
      <div className="formPage">
        <Row className="justify-content-center">
          <IconHolder icon={Step7Icon} viewBox="0 0 25 25" color='primary' />
        </Row>
        <h4 className="headerTitle">Confirming Your Existing Coverage</h4>
        <h5 className="headerSubtitle">Legal questions we're required to ask.</h5>

        <Row className="justify-content-center">
          <Col lg={5}>
            <div className="mb-3">
              <StyledRadioInput
                name="pendingApplications"
                id="pendingApplications"
                label="Does any Proposed Insured have life insurance or annuity applications pending with other companies?"
                value={props.formState.pendingApplications}
                options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                onChange={e => handleValueChange('pendingApplications', e)}
              />
            </div>
            <div className="mb-3">
              <StyledRadioInput
                name="existingCoverage"
                id="existingCoverage"
                label="Is there any existing life insurance or annuity insurance coverage on the life of any Proposed Insured?"
                value={props.formState.existingCoverage}
                options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                onChange={e => handleValueChange('existingCoverage', e)}
              />
            </div>
            {props.formState.existingCoverage === "true" && (
              <div>
                <div className="mb-3">
                  <TextInput
                    name="totalInsuredAmountExternal"
                    id="totalInsuredAmountExternal"
                    label="Total Death Benefit"
                    value={props.formState.totalInsuredAmountExternal}
                    onChange={e => props.onFieldChange('totalInsuredAmountExternal', e.target.value)}
                    InputProps={{
                      inputComponent: NumberFormatDollars,
                    }}
                  />
                </div>
                <div className="mb-3">
                  <StyledRadioInput
                    name="replaceExistingCoverage"
                    id="replaceExistingCoverage"
                    label="Will the insurance applied for replace or otherwise reduce in value any existing life insurance or annuity in force?"
                    value={props.formState.replaceExistingCoverage}
                    options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                    onChange={e => handleValueChange('replaceExistingCoverage', e)}
                  />
                </div>
                {props.formState.replaceExistingCoverage === "true" && (
                  <div className="mb-3">
                    {
                      props.showValidationMessage && (
                        <div className="text-danger mb-1">Replacement death benefit amount should not be greater than the total death benefit indicated above.</div>)
                    }
                    {formPolicies}
                    {props.formState.policies && props.formState.policies.length < 6 ? (
                      <Button color="primary" onClick={() => addNewPolicy()} className="d-flex m-auto">
                        ADD ANOTHER POLICY
                      </Button>) :
                      (
                        <div className="text-danger text-center">No additional policy may be added in the eApplication.</div>
                      )}
                  </div>
                )}
                {state === FLORIDA && (
                  <div className="mb-3">
                    <StyledRadioInput
                      name="receiveComparativeInfoForm"
                      id="receiveComparativeInfoForm"
                      label="Florida regulations give you the right to receive a written Comparative Info Form which summarizes your policy values. Indicate whether or not you wish a Comparative Information Form from the proposed company and your existing insurer or insurers."
                      value={props.formState.receiveComparativeInfoForm}
                      options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                      onChange={e => handleValueChange('receiveComparativeInfoForm', e)}
                    />
                  </div>
                )}

                {props.formState.replaceExistingCoverage === "true" && (
                  <div>
                    {state === MASSACHUSETTS && (
                      <div className="mb-3">
                        <CheckboxInput
                          name="yieldIndicesForCashValue"
                          id="yieldIndicesForCashValue"
                          checked={ConvertStringToBoolean(props.formState.yieldIndicesForCashValue)}
                          onChange={e => handleValueChange('yieldIndicesForCashValue', ConvertBooleanToString(e.target.checked))}
                          label="Check box to request yield indices for cash value policies"
                        />
                      </div>
                    )}
                    {!excludeReplacingReasonStates.includes(state) && (
                      <div>
                        <div className="mb-3">
                          <TextInput
                            name="reasonOfReplacement"
                            id="reasonOfReplacement"
                            label="Reason for Replacement"
                            value={props.formState.reasonOfReplacement}
                            onChange={e => props.onFieldChange('reasonOfReplacement', e.target.value)}
                            multiline={true}
                            rows="5"
                            InputLabelProps={{ className: 'w-max-content' }}
                          />
                        </div>
                      </div>
                    )}

                    <Row className="justify-content-center">
                      <Col lg={12}>
                        {(state === WYOMING) &&
                          <div>
                            <Card className="agreementCard">
                              <CardContent className="scrollbar">
                                <div className="overflow">
                                  <p className="agreementCardTextBold"><u>ITEMS TO CONSIDER</u></p>
                                  <ol className="agreementCardTextBold">
                                    <li>Due to a possible change insurability status (health, occupation, or high risk recreational activities) you might be denied new coverage or the premium may be higher than a standard premium.</li>
                                    <li>The Incontestability and Suicide Clause time periods would probably begin anew in a new policy. This could possibly result in a claim  being denied that might otherwise have been paid under an existing policy or contract.</li>
                                    <li>Your present insurance company may be able to modify your existing plan on terms which may be more favorable for you than  completely replacing it with a new policy or contract.</li>
                                    <li>Don’t terminate or alter your existing policy until after the new policy has been delivered to you and accepted by you.</li>
                                    <li>If you terminate your existing policy, you may incur surrender charges and/or penalties. The new policy you are purchasing may have a  new surrender charge period and/or sales charges.</li>
                                    <li>There may be tax consequences in replacing an existing policy. Is there a benefit from favorable “grandfathered” treatment of the old  policy under federal tax code? Is this transaction a “tax-free” exchange? See your tax advisor for specific tax advice regarding the  proposed transaction.</li>
                                    <li>Take your time in making your decision about purchasing the new policy. Gather all information about the new policy and compare it to  your old policy. Remember, you do not have to make a decision during the first meeting with your sales person.</li>
                                    <li> REMEMBER: Following receipt of a new life insurance policy or annuity contract you should immediately examine its contents. If you are  not satisfied with it for any reason, you have the right to return it within the thirty (30) day “examination period” to the insurer at its home  office or branch office or to the producer through whom it was purchased, for a full refund of premium. If you do return the policy or contract, you should request a dated receipt indicating that it was Returned.</li>
                                  </ol>
                                </div>
                              </CardContent>
                            </Card>
                            <div className="mb-3">
                              <CheckboxInput
                                name="ownerItemsToConsider"
                                id="ownerItemsToConsider"
                                checked={ConvertStringToBoolean(props.formState.ownerItemsToConsider)}
                                isValueString
                                onChange={e => props.onFieldChange('ownerItemsToConsider', ConvertBooleanToString(e.target.checked))}
                                label={`I read the "Items to Consider"`}
                                className="setMargin"
                                labelClassName="align-items-start"
                              />
                            </div>
                          </div>
                        }
                      </Col>
                    </Row>

                    {stateList.includes(state) && (
                      <div>
                        <div className="mb-3">
                          <StyledRadioInput
                            name="terminateExistingPolicy"
                            id="terminateExistingPolicy"
                            label="Are you considering discontinuing making premium payments, surrendering, forfeiting, assigning to the insurer, or otherwise terminating your existing policy or contract?"
                            value={props.formState.terminateExistingPolicy}
                            options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                            onChange={e => handleValueChange('terminateExistingPolicy', e)}
                          />
                        </div>
                        <div className="mb-3">
                          <StyledRadioInput
                            name="useExistingPolicyFunds"
                            id="useExistingPolicyFunds"
                            label="Are you considering using funds from your existing policies or contracts to pay premiums due on the new policy or contract?"
                            value={props.formState.useExistingPolicyFunds}
                            options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                            onChange={e => handleValueChange('useExistingPolicyFunds', e)}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="mb-3">
              <FormControl component="fieldset">
                <FormLabel component="legend" className="labeFont">Under penalties of perjury, I as the Owner certify that (check all that apply):</FormLabel>
                <div className="mb-0">
                  <CheckboxInput
                    name="usCitizen"
                    id="usCitizen"
                    checked={ConvertStringToBoolean(props.formState.usCitizen)}
                    onChange={e => props.onFieldChange('usCitizen', ConvertBooleanToString(e.target.checked))}
                    label="I am a U.S. citizen or other U.S. person, and the number shown on this form is my correct taxpayer identification number (or I am waiting for a number to be issued to me), and,"
                    className="setMargin"
                    labelClassName="align-items-start"
                  />
                </div>
                <div className="mb-0">
                  <CheckboxInput
                    name="backupWitholding"
                    id="backupWitholding"
                    checked={ConvertStringToBoolean(props.formState.backupWitholding)}
                    onChange={e => props.onFieldChange('backupWitholding', ConvertBooleanToString(e.target.checked))}
                    label="I am not subject to backup withholding because: (a) I am exempt from backup withholding, or (b) I have not been notified by the Internal Revenue Service (IRS) that I am subject to backup withholding as a result of a failure to report all interest or dividends, or (c) the IRS has notified me that I am no longer subject to backup withholding."
                    className="setMargin"
                    labelClassName="align-items-start"
                  />
                </div>
              </FormControl>
            </div>
          </Col>
        </Row>
      </div>
    </Container>
  );
}

function PolicyInformation(props) {

  const onFieldChange = (field, value) => {
    props.onChange({ ...props.formState, [field]: value });
  };

  const handleValueChange = (field, value, longFieldName) => {
    onFieldChange(field, value);
    props.trigger(longFieldName);
  };

  return (
    <div>
      <div className='mb-3'>
        <TextInput
          name={`policies[${props.index}].insuredName`}
          id={`policies[${props.index}].insuredName`}
          label="Insured's Name"
          value={props.formState.insuredName}
          onChange={e => handleValueChange('insuredName', e.target.value, `policies[${props.index}].insuredName`)}
        />
      </div>
      <div className='mb-3'>
        <SelectInput
          name={`policies[${props.index}].company`}
          id={`policies[${props.index}].company`}
          label="Company"
          value={props.formState.company}
          onChange={e => handleValueChange('company', e.target.value, `policies[${props.index}].company`)}
          selectOptions={InsuranceProviders()}
        />
      </div>
      {
        props.formState.company === "Other" &&
        <div className='mb-3'>
          <TextInput
            name={`policies[${props.index}].insurerName`}
            id={`policies[${props.index}].insurerName`}
            label="Insurer Name"
            value={props.formState.insurerName}
            onChange={e => handleValueChange('insurerName', e.target.value, `policies[${props.index}].insurerName`)}
          />
        </div>
      }
      <div className='mb-3'>
        <TextInput
          name={`policies[${props.index}].policyNumber`}
          id={`policies[${props.index}].policyNumber`}
          label="Policy Number (or type 'Unknown')"
          value={props.formState.policyNumber}
          onChange={e => handleValueChange('policyNumber', e.target.value, `policies[${props.index}].policyNumber`)}
        />
      </div>
      <div className='mb-3'>
        <TextInput
          name={`policies[${props.index}].ownerName`}
          id={`policies[${props.index}].ownerName`}
          label="Owner's Name"
          value={props.formState.ownerName}
          onChange={e => handleValueChange('ownerName', e.target.value, `policies[${props.index}].ownerName`)}
        />
      </div>
      <div className='mb-3'>
        <TextInput
          name={`policies[${props.index}].insuranceDate`}
          id={`policies[${props.index}].insuranceDate`}
          label="Policy Effective Date"
          value={props.formState.insuranceDate}
          onChange={(e) => handleValueChange('insuranceDate', e.target.value, `policies[${props.index}].insuranceDate`)}
          InputProps={{
            inputComponent: InputMaskDate,
          }}
        />
      </div>

      <div className='mb-3'>
        <TextInput
          name={`policies[${props.index}].faceAmount`}
          id={`policies[${props.index}].faceAmount`}
          label="Death Benefit"
          value={props.formState.faceAmount}
          onChange={(e) => handleValueChange('faceAmount', e.target.value, `policies[${props.index}].faceAmount`)}
          InputProps={{
            inputComponent: NumberFormatDollars,
          }}
        />
      </div>

      {props.showPolicyRemove &&
        <div className='mb-3'>
          <Button onClick={props.onRemove}>Remove this Policy</Button>
        </div>
      }
    </div>
  );
}

PolicyPurposeConfirmation.propTypes = {
  formState: PropTypes.object.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  fullFormState: PropTypes.object,
  register: PropTypes.func,
  setValue: PropTypes.func,
  trigger: PropTypes.func,
  showValidationMessage: PropTypes.bool,
};

PolicyInformation.propTypes = {
  formState: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  onRemove: PropTypes.func,
  showPolicyRemove: PropTypes.bool,
  trigger: PropTypes.func,
};

PolicyPurposeConfirmation.ValidationSchema = (birthdate) => {
  return object().shape({
    pendingApplications: string()
      .required('This field is required.'),
    existingCoverage: string().required('This field is required.'),
    usCitizen: string().required(),
    backupWitholding: string().required(),
    replaceExistingCoverage: string().when('existingCoverage', {
      is: "true",
      then: (schema) => schema.required('This field is required.'),
      otherwise: (schema) => schema.notRequired()
    }),
    terminateExistingPolicy: string().when(['existingCoverage', 'replaceExistingCoverage', 'state'],
      ([existingCoverage, replaceExistingCoverage, state], schema) => {
        return existingCoverage === "true" && replaceExistingCoverage === "true" && stateList.includes(state)
          ? schema.required('This field is required.')
          : schema.notRequired();
      }
    ),
    useExistingPolicyFunds: string().when(['existingCoverage', 'replaceExistingCoverage', 'state'],
      ([existingCoverage, replaceExistingCoverage, state], schema) => {
        return existingCoverage === "true" && replaceExistingCoverage === "true" && stateList.includes(state)
          ? string().when('terminateExistingPolicy', (terminateExistingPolicy, innerSchema) => {
            return terminateExistingPolicy === "false"
              ? innerSchema.required('This field is required.').matches(/^true/,
                "Financial and Replacement questions are answered no, but replacement was indicated on this policy. Please resolve discrepancy.")
              : innerSchema.required('This field is required.');
          })
          : schema.notRequired();
      }
    ),
    policies: mixed().when('existingCoverage', {
      is: "true",
      then: (schema) => schema.when('replaceExistingCoverage', {
        is: "true",
        then: (schema) => yuparray(object().shape({
          insuredName: string().required('Insured name is required.'),
          company: string()
            .required('Company is required.')
            .notOneOf(['Americo Financial', 'Great Southern'],
              'Coverage is not available when replacing a policy from either Great Southern or Americo Financial.'),
          insurerName: string().when('company', {
            is: 'Other',
            then: (schema) => schema.required('Insurer Name is required.'),
            otherwise: (schema) => schema.notRequired()
          }),
          ownerName: string().required('Owner name is required.'),
          insuranceDate: string()
            .transform(emptyStringToUndefined)
            .typeError('Invalid date.')
            .test('validDate', 'Please enter a valid date.', (value) => isValid(new Date(value)))
            .test('dateTest', 'Please verify Policy Effect Date.', (value) => {
              if (!value) return false;
              const year = value.split('/')[2];
              if (parseInt(year, 10) < 1850) return false;
              if (isBefore(new Date(value), new Date(birthdate))) return false;
              if (isAfter(new Date(value), new Date())) return false;
              return true;
            })
            .required('Policy Effective Date is required.'),
          faceAmount: string().required('Death Benefit is required.'),
          replacement: string().notRequired(),
          policyNumber: string().required('Policy Number is required.'),
        })),
        otherwise: (schema) => schema.notRequired(),
      }),
      otherwise: (schema) => schema.notRequired(),
    }),
    ownerItemsToConsider: mixed().when(['replaceExistingCoverage', 'state'],
      ([replaceExistingCoverage, state], schema) => {
        return replaceExistingCoverage === "true" && state === WYOMING
          ? schema.oneOf(["true"], 'You must read the Items to Consider.')
          : schema.notRequired();
      }
    ),
    yieldIndicesForCashValue: string().when(['replaceExistingCoverage', 'state'],
      ([replaceExistingCoverage, state], schema) => {
        return replaceExistingCoverage === "true" && state === MASSACHUSETTS
          ? schema.required("This field is required.")
          : schema.notRequired();
      }
    ),
    receiveComparativeInfoForm: string().when(['existingCoverage', 'state'],
      ([existingCoverage, state], schema) => {
        return existingCoverage === "true" && state === FLORIDA
          ? schema.required("This field is required.")
          : schema.notRequired();
      }
    ),
    reasonOfReplacement: string().when(['replaceExistingCoverage', 'state', 'existingCoverage'],
      ([replaceExistingCoverage, state, existingCoverage], schema) => {
        return existingCoverage === "true" && replaceExistingCoverage === "true" &&
          !excludeReplacingReasonStates.includes(state)
          ? schema.required('Reason for replacement is required.')
            .max(615, 'Response must be 615 characters or less.')
          : schema.notRequired();
      }
    ),
    totalInsuredAmountExternal: string().when('existingCoverage', {
      is: "true",
      then: (schema) => schema.required('Total Death Benefit is required.'),
      otherwise: (schema) => schema.notRequired()
    }),
  });
};

PolicyPurposeConfirmation.DefaultFormState = (prefill = false) => {
  return {
    pendingApplications: prefill ? "false" : "",
    existingCoverage: prefill ? "false" : "",
    replaceExistingCoverage: prefill ? "false" : "",
    usCitizen: prefill ? "true" : "false",
    backupWitholding: prefill ? "true" : "false",
    terminateExistingPolicy: prefill ? "false" : "",
    useExistingPolicyFunds: prefill ? "false" : "",
    reasonOfReplacement: prefill ? "Test" : "",
    receiveComparativeInfoForm: prefill ? "false" : "",
    yieldIndicesForCashValue: "false",
    ownerItemsToConsider: prefill ? "true" : "",
    totalInsuredAmountExternal: prefill ? "" : "",
    policies: [
      {
        insuredName: prefill ? "Demo John" : "",
        company: prefill ? "Guardian Life" : "",
        ownerName: prefill ? "Demo Rhea" : "",
        insuranceDate: prefill ? "09/19/2000" : "",
        faceAmount: prefill ? "83702" : "",
        replacement: prefill ? "External" : "",
        policyNumber: prefill ? "123213123" : "",
        insurerName: prefill ? "Other" : ""
      }
    ]
  };
};
